import React from "react";
import { COMPANY_CONFIG } from "../data/companyConfig";
import {
    Phone,
    Mail,
    MapPin,
    Clock,
    Headphones,
    ShieldCheck,
    MessageCircle,
    ArrowRight,
} from "lucide-react";

/**
 * Contact section.
 *
 * The reference design had a "Send Us an Official Message" form here, but it
 * posted nowhere — it faked an 800ms delay and invented a "TKT-xxxxxx" ticket
 * reference, telling the visitor a human would reply within 2-4 hours. There is
 * no contact endpoint on the backend, so nothing was ever sent or stored.
 *
 * It is replaced with direct channels that actually reach the desk. If a real
 * enquiry endpoint is added later, a form can come back here — with a genuine
 * ticket reference issued by the server, not by Math.random().
 */
export const ContactSection = () => {
    const whatsappHref = `https://wa.me/${COMPANY_CONFIG.phoneClean.replace(
        /[^0-9]/g,
        ""
    )}?text=${encodeURIComponent(
        "Hello Global Bizness, I have an enquiry about "
    )}`;

    const mailHref = `mailto:${COMPANY_CONFIG.email}?subject=${encodeURIComponent(
        "Global Bizness enquiry"
    )}`;

    return (
        <section
            id="contact-section"
            className="py-16 sm:py-24 bg-white text-slate-900 border-b border-slate-200"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header matching PaySwitch */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold uppercase tracking-wider mb-3">
                        <Headphones className="w-3.5 h-3.5 text-blue-600" />
                        24/7 Corporate Helpline &amp; Support
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight font-['Outfit',sans-serif]">
                        Get in Touch with Global Bizness
                    </h2>
                    <p className="mt-4 text-base text-slate-600">
                        Have questions regarding bill settlements, refunds, partner
                        onboarding, or POS hardware? Our dedicated team at Hyderabad is here
                        to assist.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Left Column: Official Contact Details & Corporate Address */}
                    <div className="lg:col-span-5 space-y-6">
                        {/* Phone Card */}
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs flex items-start gap-4 hover:border-blue-400 hover:shadow-md transition-all">
                            <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-200 text-blue-600 flex items-center justify-center shrink-0">
                                <Phone className="w-6 h-6" />
                            </div>
                            <div className="space-y-1">
                                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Helpline / Phone
                                </div>
                                <a
                                    href={`tel:${COMPANY_CONFIG.phoneClean}`}
                                    className="text-lg sm:text-xl font-extrabold text-slate-900 hover:text-blue-600 transition-colors block font-['Outfit',sans-serif]"
                                    id="contact-phone-direct"
                                >
                                    {COMPANY_CONFIG.phone}
                                </a>
                                <p className="text-xs text-slate-500">
                                    Direct call &amp; WhatsApp support available.
                                </p>
                            </div>
                        </div>

                        {/* Email Card */}
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs flex items-start gap-4 hover:border-blue-400 hover:shadow-md transition-all">
                            <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center shrink-0">
                                <Mail className="w-6 h-6" />
                            </div>
                            <div className="space-y-1">
                                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Official Support Email
                                </div>
                                <a
                                    href={`mailto:${COMPANY_CONFIG.email}`}
                                    className="text-sm sm:text-base font-bold text-slate-900 hover:text-blue-600 transition-colors break-all block"
                                    id="contact-email-direct"
                                >
                                    {COMPANY_CONFIG.email}
                                </a>
                                <p className="text-xs text-slate-500">
                                    Avg response turnaround: 2 to 4 business hours.
                                </p>
                            </div>
                        </div>

                        {/* Address Card */}
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-3 hover:border-blue-400 hover:shadow-md transition-all">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 text-blue-600 flex items-center justify-center shrink-0">
                                    <MapPin className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                        Corporate Registered Office
                                    </div>
                                    <div className="font-bold text-slate-900 text-sm">
                                        {COMPANY_CONFIG.address.building}
                                    </div>
                                </div>
                            </div>

                            <div className="text-xs text-slate-600 pl-13 space-y-1 leading-relaxed">
                                <p className="font-medium text-slate-900">
                                    {COMPANY_CONFIG.address.line1}
                                </p>
                                <p>{COMPANY_CONFIG.address.line2}</p>
                                <p className="font-semibold text-blue-700">
                                    {COMPANY_CONFIG.address.building}
                                </p>
                                <p>{COMPANY_CONFIG.address.area}</p>
                                <p>
                                    {COMPANY_CONFIG.address.city}, {COMPANY_CONFIG.address.state}{" "}
                                    - {COMPANY_CONFIG.address.pincode},{" "}
                                    {COMPANY_CONFIG.address.country}
                                </p>
                            </div>

                            <div className="pt-2 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500 pl-13">
                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                <span>{COMPANY_CONFIG.operatingHours}</span>
                            </div>
                        </div>

                        {/* Trust Note */}
                        <div className="p-4 rounded-2xl bg-blue-50 border border-blue-200 text-xs text-blue-900 flex items-start gap-2.5">
                            <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                            <div>
                                <span className="font-bold text-slate-900">
                                    Authorized Grievance Redressal:
                                </span>{" "}
                                For pending refund escalations, please include your Transaction
                                Reference ID for guaranteed resolution.
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Direct channels to the operations desk */}
                    <div className="lg:col-span-7">
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xl h-full flex flex-col">
                            <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-6">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900 font-['Outfit',sans-serif]">
                                        Talk to Us Directly
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Reach the Global Bizness operations desk on whichever
                                        channel suits you — every option below goes straight to
                                        our Hyderabad team.
                                    </p>
                                </div>
                                <Headphones className="w-5 h-5 text-blue-600 shrink-0 ml-4" />
                            </div>

                            <div className="space-y-3">
                                {/* Call */}
                                <a
                                    href={`tel:${COMPANY_CONFIG.phoneClean}`}
                                    className="group flex items-center gap-4 p-4 rounded-2xl border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-blue-300 transition-all"
                                    id="contact-action-call"
                                >
                                    <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-600/20">
                                        <Phone className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-bold text-slate-900">
                                            Call the Helpline
                                        </div>
                                        <div className="text-xs text-slate-500 truncate">
                                            {COMPANY_CONFIG.phone} • Fastest for payment
                                            escalations
                                        </div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all shrink-0" />
                                </a>

                                {/* WhatsApp */}
                                <a
                                    href={whatsappHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group flex items-center gap-4 p-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50 hover:border-emerald-400 transition-all"
                                    id="contact-action-whatsapp"
                                >
                                    <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-emerald-600/20">
                                        <MessageCircle className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-bold text-slate-900">
                                            Message Us on WhatsApp
                                        </div>
                                        <div className="text-xs text-slate-500 truncate">
                                            Share screenshots, bills &amp; transaction references
                                        </div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-700 group-hover:translate-x-1 transition-all shrink-0" />
                                </a>

                                {/* Email */}
                                <a
                                    href={mailHref}
                                    className="group flex items-center gap-4 p-4 rounded-2xl border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-blue-300 transition-all"
                                    id="contact-action-email"
                                >
                                    <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
                                        <Mail className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-bold text-slate-900">
                                            Email the Support Desk
                                        </div>
                                        <div className="text-xs text-slate-500 break-all">
                                            {COMPANY_CONFIG.email}
                                        </div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all shrink-0" />
                                </a>
                            </div>

                            {/* Partner onboarding note — onboarding is invite-only */}
                            <div className="mt-6 pt-5 border-t border-slate-100 space-y-3">
                                <div className="p-4 rounded-2xl bg-blue-50 border border-blue-200">
                                    <div className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-1">
                                        Becoming a Retailer or Distributor
                                    </div>
                                    <p className="text-xs text-slate-600 leading-relaxed">
                                        Partner accounts on the Global Bizness network are issued
                                        by invitation. Call or WhatsApp the numbers above and our
                                        regional relationship manager will assess your store and
                                        send an onboarding invite.
                                    </p>
                                </div>

                                <div className="flex items-start gap-2 text-[11px] text-slate-500">
                                    <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                                    <span>{COMPANY_CONFIG.operatingHours}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
